import { Component, Listen, h, State } from '@stencil/core';
import { LocationIFace, DisplayModalDataUpdateEvent, formatDistance, ModalUpdateEvent } from '@hpfc/fc-frontend-core';

/**
 * fccd-doctor-location-modal is a reusable modal component for showing doctor locations in new regions app to align with the new UX design guidelines.
 */
@Component({
  tag: 'fccd-doctor-location-modal',
  styleUrl: 'fccd-doctor-location-modal.scss',
})
export class HpDoctorLocationModal {
  practitionerId: number | undefined = undefined;

  /**
   * Practitioner name, including their credentials if needed.
   */
  @State()
  practitionerName: string;

  /**
   * practitionerLocations is for the list of objects with details for both HP and Non-HP Locations
   */
  @State()
  practitionerLocations: Array<LocationIFace>;

  /**
   * Indicates whether there was an error fetching the modal content.
   */
  @State()
  inErrorState: boolean = false;

  /**
   * Determines whether the locations modal is shown or not.
   */
  @State()
  showModal: boolean = false;

  @Listen('fccdDisplayModalDataUpdateEvent', { target: 'document' })
  updateHandler(event: CustomEvent<DisplayModalDataUpdateEvent>) {
    if (!this.practitionerId || this.practitionerId !== event.detail.identifier) {
      return;
    }

    if (event.detail.isError === true) {
      this.inErrorState = true;
    } else if (event.detail.data) {
      this.inErrorState = false;
      this.practitionerLocations = event.detail.data;
    }
  }

  @Listen('fccdModalUpdateEvent', { target: 'document' })
  fccdModalUpdateEventHandler(event: CustomEvent<ModalUpdateEvent>) {
    this.practitionerId = event.detail.identifier;
    this.practitionerName = event.detail.header;
    this.practitionerLocations = [];
    this.showModal = true;
  }

  @Listen('dismiss')
  @Listen('confirmed')
  onModalEvent() {
    this.showModal = false;
    this.practitionerId = undefined;
  }

  genLocation(location) {
    const convertPhone = phoneNumber => {
      if (typeof phoneNumber !== 'string') {
        return null;
      }

      return phoneNumber.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    };

    const phone = convertPhone(location.phone);
    const distance = formatDistance(location.distance);

    if (location?.isHp === true) {
      return (
        <hp5-person-card-location
          location-id={location.id}
          name={location.name}
          location-href={location.locationUrl}
          address={location.address}
          directions-text="Directions"
          directions-href={location.directionsUrl}
          primary-cta-text={phone}
          primary-cta-href={phone ? `tel:${phone}` : null}
          distance-text={location.distance ? distance : null}
        />
      );
    } else {
      return <hp5-person-card-location location-id={location.id} name={location.name} address={location.address} hide-directions phone-in-details={location.phone} />;
    }
  }

  getTitleText(inErrorState: boolean, count: number, practitionerName: string) {
    if (inErrorState === true) {
      return 'An error occurred finding locations.';
    }

    if (count === 0) {
      return 'Loading locations...';
    } else {
      return `${practitionerName} practices at ${count} location${count > 1 ? 's' : ''}`;
    }
  }

  render() {
    const { showModal, practitionerName, practitionerLocations, inErrorState } = this;

    const count = practitionerLocations?.length ?? 0;

    return (
      <hp5-modal-basic
        body-size-selection="md"
        confirm-button-text="Close"
        title-size-selection="md"
        title-text={this.getTitleText(inErrorState, count, practitionerName)}
        visible={showModal}
      >
        {practitionerLocations?.map(this.genLocation)}
      </hp5-modal-basic>
    );
  }
}
